import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getPendingUpload,
  putPendingUpload,
  clearPendingUpload,
  shiftPendingUpload,
  type PendingTemplateUpload,
  type QueuedTemplate,
} from '@/lib/idbTemplateUpload';
import { classifyCandidates, type Prediction } from '@/lib/templateClassify';
import { templatesApi, uploadToSignedUrl, type LayoutTemplateAlbumCategory } from '@/lib/api';
import { buildStrippedOverlaySvg, buildSlotsJson, slimSvgForPersistence } from '@/lib/templatePipeline';
import { AdminLayout } from '@/components/AdminLayout';
import { Button } from '@/components/ui/Button';
import { useT } from '@/lib/i18n';

/**
 * Rasterize an SVG string to a PNG Blob at a target pixel width.
 *
 * Used at save time to produce the print-resolution overlay (1500px) and the
 * preview (800px) without round-tripping through a server. Browser-only —
 * Image + canvas2D.
 *
 * No post-erase pass: the overlay SVG is already stripped (slots + page-bg)
 * on the SVG side via `buildStrippedOverlaySvg`. The previous
 * `destination-out` pass was over-aggressive — any decoration whose bbox
 * intersected a slot bbox (washi tape, olive branch) got erased along with
 * the slot area and never made it into the overlay PNG.
 */
async function rasterizeSvgToPng(svgString: string, targetWidth: number): Promise<Blob> {
  const parser = new DOMParser();
  const doc = parser.parseFromString(svgString, 'image/svg+xml');
  const root = doc.documentElement as unknown as SVGSVGElement;
  const vbW = root.viewBox.baseVal.width || root.width.baseVal.value;
  const vbH = root.viewBox.baseVal.height || root.height.baseVal.value;
  const scale = targetWidth / vbW;
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(vbW * scale);
  canvas.height = Math.round(vbH * scale);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('no 2d ctx');

  const blob = new Blob([svgString], { type: 'image/svg+xml' });
  const url = URL.createObjectURL(blob);
  try {
    const img = new Image();
    img.src = url;
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('svg→img load failed'));
    });
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  } finally {
    URL.revokeObjectURL(url);
  }

  return await new Promise<Blob>((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('canvas.toBlob → null'))), 'image/png'),
  );
}

/**
 * Full-page review route at /admin/layouts/review.
 *
 * Task 10 scope: scaffold the three-tier candidate UI + metadata form +
 * disabled save bar. No interactivity on cards (Task 11), no save handler
 * (Task 12), no re-classify button (Task 13).
 *
 * The dialog (UploadTemplateDialog) stashes a `PendingTemplateUpload` in
 * IndexedDB via putPendingUpload before navigating here. If the read returns
 * null (no payload, stale TTL, or shape mismatch), this page redirects back
 * to /admin/layouts — the route is unusable without the in-flight upload
 * state.
 */

// Confidence floor for treating an AI prediction as authoritative. Below this
// the candidate is bucketed as "uncertain" regardless of which label won.
// Mirrors the threshold the rest of the pipeline uses.
const CONFIDENCE_CUTOFF = 0.85;

type Mark = 'SLOT' | 'DECOR' | 'DELETE' | null;

interface CardState {
  id: string;
  thumbnailDataUrl: string;
  mark: Mark;
  prediction?: Prediction;
  tier: 'slot' | 'uncertain' | 'hiddenDecor';
}

function classify(prediction: Prediction | undefined): { mark: Mark; tier: CardState['tier'] } {
  if (!prediction) return { mark: null, tier: 'uncertain' };
  if (prediction.confidence < CONFIDENCE_CUTOFF) return { mark: null, tier: 'uncertain' };
  if (prediction.prediction === 'SLOT') return { mark: 'SLOT', tier: 'slot' };
  return { mark: 'DECOR', tier: 'hiddenDecor' };
}

// Match AdminLayoutTemplatesPage CATEGORY_LABELS so the admin sees the same
// French labels across the upload flow and the listing.
const CATEGORIES: { value: LayoutTemplateAlbumCategory; label: string }[] = [
  { value: 'travel',     label: 'Voyage' },
  { value: 'baby',       label: 'Bébé' },
  { value: 'wedding',    label: 'Mariage' },
  { value: 'birthday',   label: 'Anniversaire' },
  { value: 'graduation', label: 'Diplôme' },
  { value: 'pet',        label: 'Animaux' },
  { value: 'family',     label: 'Famille' },
  { value: 'religious',  label: 'Religieux' },
];

function slugify(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function SlotReviewPane() {
  const { t } = useT();
  const navigate = useNavigate();
  const [pending, setPending] = useState<PendingTemplateUpload | null>(null);
  const [cards, setCards] = useState<CardState[]>([]);
  const [showHidden, setShowHidden] = useState(false);
  // Captured once on first load — the original queue length. Used to render
  // "Template X of N" where N stays fixed across the walk while pending.queue
  // shrinks on each Save/Cancel advance. Set exactly once; subsequent reads
  // of the IDB queue (after a shift) don't update it.
  const [originalTotal, setOriginalTotal] = useState<number | null>(null);
  const active: QueuedTemplate | null = pending?.queue[0] ?? null;

  // Metadata form state — absorbed from the old UploadTemplateDialog so the
  // admin can finish naming/configuring the template before saving. Task 12
  // wires these into the create-template payload.
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  // Once the admin types into the slug box manually we stop auto-deriving
  // it from `name`. This mirrors the long-standing UX in TemplateEditDialog.
  const [slugDirty, setSlugDirty] = useState(false);
  const [category, setCategory] = useState<LayoutTemplateAlbumCategory>('travel');
  const [isVisible, setIsVisible] = useState(true);
  const [isAiEligible, setIsAiEligible] = useState(false);

  // Save flow state. `saveError` is left set on failure so the admin can read
  // the message and retry without re-uploading the SVG; IndexedDB is only
  // cleared once the create-template call succeeds.
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Re-classify button state. Useful when the first classifyCandidates call
  // timed out and the page rendered with `predictions === []`.
  const [reclassifying, setReclassifying] = useState(false);

  // Modal state for the full-size thumbnail overlay. `null` = closed.
  const [modalCard, setModalCard] = useState<CardState | null>(null);

  /**
   * Seed all per-template state from a queue head. Runs on initial mount AND
   * after each Save/Cancel advance — keeps the form, cards, slugDirty flag,
   * and save-error in sync with the new active template.
   */
  function applyActiveTemplate(q: QueuedTemplate) {
    const meta = q.initialMetadata;
    const baseName = meta?.name ?? q.fileName.replace(/\.svg$/i, '');
    setName(baseName);
    setSlug(meta?.slug ?? slugify(baseName));
    // Reset slugDirty per-template: a fresh template should auto-derive slug
    // from name unless this particular template arrived with an explicit
    // pre-existing slug (TemplateEditDialog Replace-SVG flow).
    setSlugDirty(!!meta?.slug);
    setCategory(meta?.category ?? 'travel');
    setIsVisible(typeof meta?.isVisible === 'boolean' ? meta.isVisible : true);
    setIsAiEligible(typeof meta?.isAiEligible === 'boolean' ? meta.isAiEligible : false);
    // Clear any save error from the prior template.
    setSaveError(null);
    // Build the per-candidate card state by zipping predictions in.
    const predById = new Map(q.predictions.map((p) => [p.id, p]));
    let built = q.candidates.map((c) => {
      const prediction = predById.get(c.id);
      const { mark, tier } = classify(prediction);
      return { id: c.id, thumbnailDataUrl: c.thumbnailDataUrl, mark, prediction, tier };
    });
    // ZERO-SLOT FALLBACK: on texture-heavy collages (vintage paper, marble,
    // scrapbook) the AI marks every candidate DECOR → no photo slot → the
    // template can't be saved. Rather than block the import, default EVERY
    // detected picture box to a photo SLOT so the template still lands in the
    // library; the admin corrects any wrong slot afterwards. Only fires when
    // the AI found nothing — normally-classified templates are untouched.
    if (!built.some((c) => c.mark === 'SLOT')) {
      built = built.map((c) => ({ ...c, mark: 'SLOT' as const, tier: 'slot' as const }));
    }
    setCards(built);
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const parsed = await getPendingUpload();
      if (cancelled) return;
      if (!parsed) {
        navigate('/admin/layouts');
        return;
      }
      // Capture original queue length once. Subsequent re-loads (after a
      // Save/Cancel advance) shrink queue.length but originalTotal stays
      // put so the "X of N" counter increments correctly.
      setOriginalTotal((prev) => (prev === null ? parsed.queue.length : prev));
      setPending(parsed);
      applyActiveTemplate(parsed.queue[0]);
    })().catch(() => {
      if (!cancelled) navigate('/admin/layouts');
    });
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  // Keep slug in sync with name until the admin explicitly edits the slug.
  useEffect(() => {
    if (!slugDirty) setSlug(slugify(name));
  }, [name, slugDirty]);

  const slotCards = useMemo(() => cards.filter((c) => c.tier === 'slot'), [cards]);
  const uncertainCards = useMemo(() => cards.filter((c) => c.tier === 'uncertain'), [cards]);
  const hiddenCards = useMemo(() => cards.filter((c) => c.tier === 'hiddenDecor'), [cards]);

  function setMark(id: string, mark: Mark) {
    setCards((prev) => prev.map((c) => (c.id === id ? { ...c, mark } : c)));
  }

  // Reorder a SLOT card within the SLOT-only subsequence. DECOR and uncertain
  // cards keep their absolute positions in the array — only the relative order
  // of SLOT cards changes. The save handler (Task 12) walks `cards` in order
  // and pulls out SLOT entries to build `slots[]`, so this controls reading
  // order without touching unrelated cards.
  function moveSlot(id: string, direction: -1 | 1) {
    setCards((prev) => {
      const slotIds = prev.filter((c) => c.mark === 'SLOT').map((c) => c.id);
      const i = slotIds.indexOf(id);
      const j = i + direction;
      if (i < 0 || j < 0 || j >= slotIds.length) return prev;
      const reorderedSlotIds = [...slotIds];
      [reorderedSlotIds[i], reorderedSlotIds[j]] = [reorderedSlotIds[j], reorderedSlotIds[i]];
      const byId = new Map(prev.map((c) => [c.id, c]));
      const result: CardState[] = [];
      let slotCursor = 0;
      for (const c of prev) {
        if (c.mark === 'SLOT') {
          result.push(byId.get(reorderedSlotIds[slotCursor++])!);
        } else {
          result.push(c);
        }
      }
      return result;
    });
  }

  // Re-run the AI classifier against the same candidates. Resets card state
  // to the fresh predictions, overwriting any manual toggles the admin made.
  // We confirm before doing that — unless the first call failed (predictions
  // is empty), in which case there's nothing for the admin to lose.
  async function handleReclassify() {
    if (!pending || !active) return;
    if (
      active.predictions.length > 0 &&
      !window.confirm(
        t('Relancer la classification IA va réinitialiser vos modifications manuelles. Continuer ?'),
      )
    ) {
      return;
    }
    setReclassifying(true);
    try {
      const predictions = await classifyCandidates(active.candidates);
      // Persist by rewriting the queue head with fresh predictions; tail
      // templates are left untouched.
      const updatedQueue = [{ ...active, predictions }, ...pending.queue.slice(1)];
      await putPendingUpload({ queue: updatedQueue });
      setPending({ queue: updatedQueue, savedAt: Date.now() });
      const predById = new Map(predictions.map((p) => [p.id, p]));
      setCards((prev) =>
        prev.map((c) => {
          const prediction = predById.get(c.id);
          const { mark, tier } = classify(prediction);
          return { ...c, mark, prediction, tier };
        }),
      );
    } finally {
      setReclassifying(false);
    }
  }

  // Esc closes the thumbnail modal. Effect re-binds whenever `modalCard`
  // toggles so the listener is only attached while the modal is open.
  useEffect(() => {
    if (!modalCard) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setModalCard(null);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [modalCard]);

  /**
   * Drop the head of the queue (Save success or Cancel) and either advance
   * to the next template or — when the queue is empty — clean up and return
   * to the templates list. Centralizing this here means Save and Cancel
   * share identical advance behavior; the only difference is whether they
   * persist the current template first.
   */
  async function advanceQueue() {
    const next = await shiftPendingUpload();
    if (!next) {
      navigate('/admin/layouts');
      return;
    }
    setPending(next);
    applyActiveTemplate(next.queue[0]);
  }

  // Cancel skips just THIS template — the next one in the queue (if any)
  // takes the screen. Single-Cancel-button UX per spec; if the admin meant
  // to abandon the whole batch they can keep clicking Cancel through it,
  // or close the tab (the IDB record TTLs out after 1h).
  async function handleCancel() {
    try {
      await advanceQueue();
    } catch {
      // Worst case: we couldn't advance. Fall back to the old behavior of
      // wiping the whole IDB record so the admin isn't stuck in a loop.
      try { await clearPendingUpload(); } catch { /* best-effort */ }
      navigate('/admin/layouts');
    }
  }

  async function handleSave() {
    if (!pending || !active) return;
    setSaving(true);
    setSaveError(null);
    try {
      // 1. Pure transforms. `cards` ordering controls slot reading order via
      // moveSlot — preserve that here by walking `cards` rather than the
      // original `active.candidates` array.
      const confirmedCards = cards.filter((c) => c.mark === 'SLOT');
      const confirmedSlotIds = confirmedCards.map((c) => c.id);
      // Strip uses domIndex (stable across re-parses), not id. Look up the
      // original Candidate by id so we get its detection-time domIndex.
      const candidatesById = new Map(active.candidates.map((c) => [c.id, c]));
      const confirmedSlotIndices = confirmedCards
        .map((c) => candidatesById.get(c.id)?.domIndex)
        .filter((n): n is number => typeof n === 'number');
      // DELETE — elements the admin removed entirely (neither slot nor decor).
      // Their <image> is stripped from BOTH the overlay AND the persisted
      // original SVG, so they render nowhere.
      const deletedIndices = cards
        .filter((c) => c.mark === 'DELETE')
        .map((c) => candidatesById.get(c.id)?.domIndex)
        .filter((n): n is number => typeof n === 'number');
      // Strip the confirmed <image> slots + deleted elements + any page-bg
      // <rect>/<path>. The slot strip removes Canva stock photos (→ fillable
      // placeholders); the delete strip removes unwanted décor entirely.
      // Decorations that overlapped slot bboxes (washi tape, branches) are
      // preserved — they sit on top of customer photos via overlay z-index.
      const overlaySvg = buildStrippedOverlaySvg(
        active.svgString,
        [...confirmedSlotIndices, ...deletedIndices],
        // Defensive: an IDB record from a prior deploy might lack this field
        // (TTL is 1h, but tabs left open from before-deploy still hit this).
        active.pageBackgrounds ?? [],
      );
      const slots = buildSlotsJson(confirmedSlotIds, active.candidates);

      // 2. Rasterize: 1500px overlay (print, transparent where slots + page
      // bg were stripped) + 800px preview (gallery thumb of the original
      // unstripped SVG so admins see what the template actually looks like).
      const overlayPng = await rasterizeSvgToPng(overlaySvg, 1500);
      // Preview thumbnail: rasterise the ORIGINAL minus deleted elements so the
      // gallery thumb reflects removals (slots stay visible — the customer sees
      // their photo there). No deletions → identical to before.
      const previewSvg = deletedIndices.length
        ? slimSvgForPersistence(active.svgString, [], deletedIndices)
        : active.svgString;
      const previewPng = await rasterizeSvgToPng(previewSvg, 800);

      // 3. Upload both PNGs through the existing signed-URL flow. Wrap blobs
      // as `File` so the helper's type signature (and its xhr Content-Type
      // fallback) behave as designed.
      const baseName = slug || 'template';

      // Upload the original SVG so the customer renderer can render it
      // pixel-perfectly via SVG-href-swap (the new render path uses this
      // instead of the rasterised overlay PNG). The PNG still gets uploaded
      // below as the gallery thumbnail.
      //
      // BEFORE upload: strip the embedded Canva placeholder base64 from each
      // slot <image>. We never display those placeholders at customer time
      // (empty slots get opacity=0; filled slots get their href swapped to
      // the customer's photo data URL). Real Canva templates routinely drop
      // from ~40 MB → ~3 MB after this, with zero visual difference.
      const slimmedSvg = slimSvgForPersistence(active.svgString, confirmedSlotIndices, deletedIndices);
      const originalSvgBlob = new Blob([slimmedSvg], { type: 'image/svg+xml' });
      const originalSvgUpload = await templatesApi.getUploadUrl(
        `${baseName}-original.svg`,
        'image/svg+xml',
      );
      await uploadToSignedUrl(
        originalSvgUpload.upload_url,
        new File([originalSvgBlob], `${baseName}-original.svg`, { type: 'image/svg+xml' }),
        'image/svg+xml',
      );

      const overlayUpload = await templatesApi.getUploadUrl(
        `${baseName}-overlay-print.png`,
        'image/png',
      );
      await uploadToSignedUrl(
        overlayUpload.upload_url,
        new File([overlayPng], `${baseName}-overlay-print.png`, { type: 'image/png' }),
        'image/png',
      );

      const previewUpload = await templatesApi.getUploadUrl(
        `${baseName}-preview.png`,
        'image/png',
      );
      await uploadToSignedUrl(
        previewUpload.upload_url,
        new File([previewPng], `${baseName}-preview.png`, { type: 'image/png' }),
        'image/png',
      );

      // 4. Persist the template. The original SVG is uploaded above so the
      // SVG-href-swap render path can render templates pixel-perfectly; the
      // printable overlay PNG + preview PNG are kept for the legacy
      // TemplatedPhotoSlot fallback during migration. overlay_svg_path
      // remains unused for now (stays null).
      //
      // Task 14: when editingTemplateId is set we PATCH the existing row
      // (the admin clicked "Remplacer le SVG" on an existing template);
      // otherwise we POST a new one (UploadTemplateDialog create flow).
      // Persist the page background color so the customer-facing page bg
      // matches the template's native page color (cream/paper/whatever
      // Canva used). Default to white when detection found no page bg.
      const backgroundColor = active.pageBackgroundColor ?? '#FFFFFF';
      if (active.editingTemplateId) {
        await templatesApi.update(active.editingTemplateId, {
          name,
          slug,
          album_category: category,
          is_visible: isVisible,
          is_ai_eligible: isAiEligible,
          slots,
          background_storage_path: previewUpload.storage_path,
          overlay_print_png_path: overlayUpload.storage_path,
          original_svg_path: originalSvgUpload.storage_path,
          background_color: backgroundColor,
        });
      } else {
        await templatesApi.create({
          name,
          slug,
          album_category: category,
          is_visible: isVisible,
          is_ai_eligible: isAiEligible,
          slots,
          background_storage_path: previewUpload.storage_path,
          overlay_print_png_path: overlayUpload.storage_path,
          original_svg_path: originalSvgUpload.storage_path,
          background_color: backgroundColor,
          tags: [],
          page_aspect: 'A4-portrait',
        });
      }

      // 5. Success — advance the queue. If empty, advanceQueue navigates
      // back to /admin/layouts; otherwise the next template takes the
      // screen with fresh metadata + cards.
      await advanceQueue();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }

  if (!pending || !active) {
    return (
      <AdminLayout>
        <div className="p-8 text-sm text-gray-500">{t('Chargement…')}</div>
      </AdminLayout>
    );
  }

  // Position in the original batch: total - remaining + 1. Defensive
  // fallback to current queue length in the (shouldn't-happen) case
  // originalTotal wasn't seeded by the mount effect.
  const totalForDisplay = originalTotal ?? pending.queue.length;
  const positionInBatch = totalForDisplay - pending.queue.length + 1;
  const showBatchHeader = totalForDisplay > 1;

  // slotCount is the number of cards the admin has *currently* marked as
  // SLOT — across all three sections, since toggling a tier-slot card to
  // DECOR or a tier-uncertain card to SLOT both count. The section grouping
  // stays based on initial AI tier (intentional per spec); border color and
  // this counter reflect live admin intent.
  const slotCount = cards.filter((c) => c.mark === 'SLOT').length;
  const unresolvedUncertain = cards.filter(
    (c) => c.tier === 'uncertain' && c.mark === null,
  ).length;
  const canSave = slotCount > 0 && unresolvedUncertain === 0;
  const saveHint =
    slotCount === 0
      ? t('Marquez au moins 1 emplacement photo')
      : unresolvedUncertain > 0
        ? t('Confirmez {n} élément(s) incertain(s)', { n: unresolvedUncertain })
        : '';

  // Stable ordered list of SLOT card ids — used to compute per-card
  // canMoveUp/Down so the first slot can't go up and the last can't go down.
  const slotIdsForOrder = cards.filter((c) => c.mark === 'SLOT').map((c) => c.id);

  return (
    <AdminLayout>
      <div className="max-w-6xl mx-auto px-6 py-6">
        <header className="mb-6">
          {showBatchHeader && (
            <div
              role="status"
              className="mb-3 bg-brand-500/10 border border-brand-500/30 text-brand-900 text-sm px-4 py-2 rounded-lg flex items-center justify-between gap-4"
            >
              <span>
                {t('Template')} <strong>{positionInBatch}</strong> {t('sur')}{' '}
                <strong>{totalForDisplay}</strong> —{' '}
                <span className="font-mono">{active.fileName}</span>
              </span>
              <span className="text-xs text-brand-700">
                {pending.queue.length - 1 > 1
                  ? t('{n} restants après celui-ci', { n: pending.queue.length - 1 })
                  : t('{n} restant après celui-ci', { n: pending.queue.length - 1 })}
              </span>
            </div>
          )}
          <button
            type="button"
            onClick={handleCancel}
            className="text-sm text-gray-500 hover:text-gray-700 mb-2"
          >
            {t('← Retour aux modèles')}
          </button>
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">
                {active.editingTemplateId
                  ? t('Modifier le template')
                  : t('Réviser les emplacements photo')}
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                {t('Fichier :')} <span className="font-mono">{active.fileName}</span> ·{' '}
                {active.candidates.length > 1
                  ? t('{n} éléments détectés', { n: active.candidates.length })
                  : t('{n} élément détecté', { n: active.candidates.length })}
              </p>
            </div>
            <button
              type="button"
              onClick={handleReclassify}
              disabled={reclassifying || saving}
              className="text-sm px-3 py-1.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            >
              {reclassifying ? t('Classification en cours…') : t('Relancer la classification IA')}
            </button>
          </div>
          {active.predictions.length === 0 && (
            <div
              role="alert"
              className="mt-3 bg-amber-50 border border-amber-200 text-amber-800 text-sm px-4 py-3 rounded-lg"
            >
              {t('Classification IA indisponible — marquez chaque élément manuellement.')}
            </div>
          )}
        </header>

        {/* Metadata form — values not yet wired to a save handler (Task 12). */}
        <section className="mb-8 bg-white border border-gray-200 rounded-xl p-5">
          <h2 className="text-sm font-semibold text-gray-900 mb-4">{t('Informations du modèle')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label={t('Nom')}>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500"
              />
            </Field>
            <Field label={t('Slug')}>
              <input
                type="text"
                value={slug}
                onChange={(e) => {
                  setSlug(e.target.value);
                  setSlugDirty(true);
                }}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500"
              />
            </Field>
            <Field label={t('Catégorie')}>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value as LayoutTemplateAlbumCategory)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500"
              >
                {CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>
                    {t(c.label)}
                  </option>
                ))}
              </select>
            </Field>
            <div className="flex flex-col gap-2 justify-center">
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isVisible}
                  onChange={(e) => setIsVisible(e.target.checked)}
                  className="w-4 h-4 accent-brand-500"
                />
                {t('Visible dans la galerie')}
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isAiEligible}
                  onChange={(e) => setIsAiEligible(e.target.checked)}
                  className="w-4 h-4 accent-brand-500"
                />
                {t('Éligible à l’IA')}
              </label>
            </div>
          </div>
        </section>

        {/* Tier 1 — confirmed slots. */}
        <section className="mb-6">
          <h2 className="text-sm font-semibold text-gray-900 mb-3">
            {slotCount > 1
              ? t('Emplacements photo — {n} détectés', { n: slotCount })
              : t('Emplacements photo — {n} détecté', { n: slotCount })}
          </h2>
          {slotCards.length === 0 ? (
            <p className="text-xs text-gray-500 italic">
              {t('Aucun emplacement photo classifié pour l’instant.')}
            </p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {slotCards.map((c) => {
                const idx = slotIdsForOrder.indexOf(c.id);
                return (
                  <SlotCard
                    key={c.id}
                    card={c}
                    onMark={(m) => setMark(c.id, m)}
                    onMoveUp={() => moveSlot(c.id, -1)}
                    onMoveDown={() => moveSlot(c.id, +1)}
                    canMoveUp={idx > 0}
                    canMoveDown={idx >= 0 && idx < slotIdsForOrder.length - 1}
                    onOpen={() => setModalCard(c)}
                  />
                );
              })}
            </div>
          )}
        </section>

        {/* Tier 2 — uncertain, must be resolved (Task 11 will enforce). */}
        {uncertainCards.length > 0 && (
          <section className="mb-6">
            <h2 className="text-sm font-semibold text-gray-900 mb-3">
              {t('Incertains — {n} à confirmer', { n: uncertainCards.length })}
            </h2>
            <p className="text-xs text-gray-500 mb-3">
              {t('L’IA n’a pas pu décider avec assez de certitude. Marquez chaque élément ci-dessous.')}
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {uncertainCards.map((c) => (
                <SlotCard
                  key={c.id}
                  card={c}
                  onMark={(m) => setMark(c.id, m)}
                  onOpen={() => setModalCard(c)}
                />
              ))}
            </div>
          </section>
        )}

        {/* Tier 3 — confidently decorative, collapsed by default. */}
        {hiddenCards.length > 0 && (
          <section className="mb-6">
            <button
              type="button"
              onClick={() => setShowHidden((v) => !v)}
              className="text-sm text-gray-500 hover:text-gray-700 underline"
            >
              {showHidden
                ? (hiddenCards.length > 1
                    ? t('Masquer {n} décorations masquées', { n: hiddenCards.length })
                    : t('Masquer {n} décoration masquée', { n: hiddenCards.length }))
                : (hiddenCards.length > 1
                    ? t('Afficher {n} décorations masquées', { n: hiddenCards.length })
                    : t('Afficher {n} décoration masquée', { n: hiddenCards.length }))}
            </button>
            {showHidden && (
              <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {hiddenCards.map((c) => (
                  <SlotCard
                    key={c.id}
                    card={c}
                    onMark={(m) => setMark(c.id, m)}
                    onOpen={() => setModalCard(c)}
                  />
                ))}
              </div>
            )}
          </section>
        )}

        {/* Save bar — strip + rasterize + create flow is wired in Task 12.
            On failure we keep the page mounted so the admin can retry without
            re-uploading the SVG (the IDB payload stays populated). */}
        <footer className="mt-8 pt-6 border-t border-gray-200 flex items-center justify-end gap-3">
          {saveError && (
            <span role="alert" className="text-sm text-red-600 mr-auto">
              {saveError}
            </span>
          )}
          <Button
            variant="secondary"
            onClick={handleCancel}
            disabled={saving}
          >
            {t('Annuler')}
          </Button>
          <Button onClick={handleSave} disabled={!canSave || saving} title={saveHint}>
            {saving
              ? t('Enregistrement…')
              : slotCount > 1
                ? t('Enregistrer le template ({n} emplacements)', { n: slotCount })
                : t('Enregistrer le template ({n} emplacement)', { n: slotCount })}
          </Button>
        </footer>

        {modalCard && (
          <div
            role="dialog"
            aria-label={t('Aperçu agrandi')}
            onClick={() => setModalCard(null)}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.75)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              cursor: 'zoom-out',
            }}
          >
            <img
              src={modalCard.thumbnailDataUrl}
              alt={modalCard.id}
              style={{ maxWidth: '90vw', maxHeight: '90vh' }}
            />
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      {children}
    </div>
  );
}

function SlotCard({
  card,
  onMark,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
  onOpen,
}: {
  card: CardState;
  onMark: (mark: Mark) => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  canMoveUp?: boolean;
  canMoveDown?: boolean;
  onOpen?: () => void;
}) {
  const { t } = useT();
  // Border color tracks the *current* admin mark, not the initial AI tier —
  // toggling a card flips its color immediately so the admin sees what
  // they've decided regardless of which section the card sits in.
  const borderClass =
    card.mark === 'SLOT'
      ? 'border-2 border-emerald-500'
      : card.mark === 'DECOR'
        ? 'border border-gray-300'
        : card.mark === 'DELETE'
          ? 'border-2 border-red-400'
          : 'border-2 border-amber-500';

  return (
    <div
      data-testid="slot-card"
      data-mark={card.mark ?? 'uncertain'}
      data-tier={card.tier}
      className={`${borderClass} bg-white rounded-lg p-2 flex flex-col`}
    >
      <button
        type="button"
        onClick={onOpen}
        disabled={!onOpen}
        aria-label={t('Agrandir {id}', { id: card.id })}
        className="aspect-square bg-gray-50 rounded overflow-hidden flex items-center justify-center w-full p-0 border-0 cursor-zoom-in disabled:cursor-default"
      >
        <img
          src={card.thumbnailDataUrl}
          alt={card.id}
          className={`max-w-full max-h-full object-contain ${card.mark === 'DELETE' ? 'opacity-30 grayscale' : ''}`}
        />
      </button>
      <div className="mt-2 text-[10px] font-mono text-gray-500 truncate" title={card.id}>
        {card.id}
      </div>
      <div className="mt-2 flex flex-col gap-1 text-[11px] text-gray-700">
        <label className="flex items-center gap-1 cursor-pointer">
          <input
            type="radio"
            name={`mark-${card.id}`}
            checked={card.mark === 'SLOT'}
            onChange={() => onMark('SLOT')}
            className="accent-emerald-500"
          />
          {t('Emplacement')}
        </label>
        <label className="flex items-center gap-1 cursor-pointer">
          <input
            type="radio"
            name={`mark-${card.id}`}
            checked={card.mark === 'DECOR'}
            onChange={() => onMark('DECOR')}
            className="accent-gray-500"
          />
          {t('Décoration')}
        </label>
        {/* DELETE — remove this element from the template entirely: neither a
            photo slot nor kept as decoration. Use for wrongly-detected boxes
            or décor you don't want. */}
        <label className="flex items-center gap-1 cursor-pointer text-red-600">
          <input
            type="radio"
            name={`mark-${card.id}`}
            checked={card.mark === 'DELETE'}
            onChange={() => onMark('DELETE')}
            className="accent-red-500"
          />
          🗑 {t('Supprimer')}
        </label>
      </div>
      {card.mark === 'SLOT' && (onMoveUp || onMoveDown) && (
        <div className="mt-2 flex gap-1">
          <button
            type="button"
            disabled={!canMoveUp}
            onClick={onMoveUp}
            aria-label={t('Monter')}
            className="px-2 py-0.5 text-xs border border-gray-200 rounded disabled:opacity-40 hover:bg-gray-50"
          >
            ↑
          </button>
          <button
            type="button"
            disabled={!canMoveDown}
            onClick={onMoveDown}
            aria-label={t('Descendre')}
            className="px-2 py-0.5 text-xs border border-gray-200 rounded disabled:opacity-40 hover:bg-gray-50"
          >
            ↓
          </button>
        </div>
      )}
      {card.prediction && (
        <small
          className="block text-[11px] text-gray-600 mt-1"
          title={card.prediction.reasoning}
        >
          {t('IA :')} {card.prediction.prediction} ·{' '}
          {Math.round(card.prediction.confidence * 100)}%
        </small>
      )}
    </div>
  );
}
